import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function EmailApprovals() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['/api/admin/email-change-requests'],
    queryFn: () => apiRequest('/api/admin/email-change-requests?status=pending'),
  });
  const pendingRequests = pendingData?.requests || [];

  const { data: resolvedData } = useQuery({
    queryKey: ['/api/admin/email-change-requests', 'resolved'],
    queryFn: () => apiRequest('/api/admin/email-change-requests?status=approved'),
  });
  const { data: rejectedData } = useQuery({
    queryKey: ['/api/admin/email-change-requests', 'rejected'],
    queryFn: () => apiRequest('/api/admin/email-change-requests?status=rejected'),
  });
  const resolvedRequests = [
    ...(resolvedData?.requests || []),
    ...(rejectedData?.requests || [])
  ].sort((a: any, b: any) => new Date(b.resolvedAt).getTime() - new Date(a.resolvedAt).getTime());

  const resolveMutation = useMutation({
    mutationFn: async ({ id, action }: { id: number, action: 'approve' | 'reject' }) => {
      return await apiRequest(`/api/admin/email-change-requests/${id}/${action}`, {
        method: 'POST'
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email-change-requests'] });
      toast({
        title: variables.action === 'approve' ? "Request Approved" : "Request Rejected",
        description: variables.action === 'approve'
          ? "The user's email has been updated."
          : "The email change request has been rejected."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Action Failed",
        description: error.message || "There was an error processing this request.",
        variant: "destructive"
      });
    }
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Email Approvals</h1>
        <p className="text-muted-foreground mt-1">
          Review and approve email address change requests from your team.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Pending Requests
            {pendingRequests.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">({pendingRequests.length})</span>
            )}
          </CardTitle>
          <CardDescription>
            These require your approval before the user's email is changed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {pendingLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : pendingRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending email change requests.</p>
          ) : (
            pendingRequests.map((request: any) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-slate-50 dark:bg-slate-800"
              >
                <div className="text-sm">
                  <p className="font-medium">{request.currentEmail} → {request.requestedEmail}</p>
                  <p className="text-xs text-muted-foreground">
                    Requested {new Date(request.requestedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => resolveMutation.mutate({ id: request.id, action: 'approve' })}
                    disabled={resolveMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resolveMutation.mutate({ id: request.id, action: 'reject' })}
                    disabled={resolveMutation.isPending}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {resolvedRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Decisions</CardTitle>
            <CardDescription>Previously approved or rejected requests.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {resolvedRequests.slice(0, 20).map((request: any) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-3 rounded-lg border text-sm"
              >
                <div>
                  <p>{request.currentEmail} → {request.requestedEmail}</p>
                  <p className="text-xs text-muted-foreground">
                    Resolved {request.resolvedAt ? new Date(request.resolvedAt).toLocaleString() : ''}
                    {request.rejectionReason ? ` — ${request.rejectionReason}` : ''}
                  </p>
                </div>
                <span className={request.status === 'approved' ? 'text-emerald-600' : 'text-red-600'}>
                  {request.status === 'approved' ? 'Approved' : 'Rejected'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
